import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Loader, AlertCircle, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ['IELTS', 'GMAT', 'VCE', 'Selective Entry', 'CBC', 'Coding', 'Other'];

const StudentSignupPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const refCodeRaw = (searchParams.get('ref') || '').trim().toUpperCase();
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', confirm: '',
    phone: '', parent_email: '', program: 'IELTS', subject_interest: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/signup/student`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || null,
        parent_email: form.parent_email.trim() || null,
        program: form.program,
        subject_interest: form.subject_interest.trim() || null,
        referral_code: refCodeRaw || null,
      }, { withCredentials: true });
      await checkAuth();
      toast.success(refCodeRaw
        ? `Welcome — you joined via referral ${refCodeRaw}!`
        : 'Welcome to Winhouse — your account is ready.');
      navigate('/student/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create account.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#FAF9F6]" data-testid="student-signup-page">
      <div className="lg:w-5/12 bg-black text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div>
          <div className="text-[#D3D86F] text-xs uppercase tracking-[3px] font-bold mb-2">WINHOUSE TUTORS</div>
          <div className="text-3xl lg:text-4xl font-bold leading-tight">Student Signup</div>
          <div className="text-sm text-gray-300 mt-3 max-w-md">Create your free Winhouse student account in seconds and get matched with the right tutor.</div>
          <ul className="mt-8 space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Instant account — no waitlist.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Lesson recaps + AI homework.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Branded PDFs and resources.</li>
          </ul>
        </div>
        <div className="mt-10 lg:mt-0">
          <div className="flex items-center gap-2 text-[#D3D86F]"><Sparkles className="h-3 w-3" /><span className="text-[10px] uppercase tracking-[2px]">IELTS · GMAT · VCE · Coding · CBC</span></div>
        </div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-[#D3D86F]/10 blur-3xl" />
      </div>

      <div className="lg:w-7/12 flex items-center justify-center p-8 lg:p-14">
        <form onSubmit={submit} className="w-full max-w-xl space-y-4" data-testid="student-signup-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[2px] text-gray-500 font-bold">
            <GraduationCap className="h-4 w-4 text-red-700" /> Create student account
          </div>

          {refCodeRaw && (
            <div className="flex items-center gap-2 bg-[#D3D86F]/15 border border-[#D3D86F]/40 rounded-lg px-3 py-2 text-xs"
              data-testid="signup-referral-banner">
              <Sparkles className="h-4 w-4 text-red-700" />
              <span className="font-bold text-black">You were invited by a Winhouse student — referral <code className="bg-black text-[#D3D86F] px-1.5 py-0.5 rounded font-mono text-[10px]" data-testid="signup-referral-code">{refCodeRaw}</code> will be applied.</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" v={form.full_name} on={(v) => u('full_name', v)} required testid="signup-full-name" />
            <Field label="Email" type="email" v={form.email} on={(v) => u('email', v)} required testid="signup-email" />
            <Field label="Password" type="password" v={form.password} on={(v) => u('password', v)} required testid="signup-password" hint="Min 8 characters" />
            <Field label="Confirm password" type="password" v={form.confirm} on={(v) => u('confirm', v)} required testid="signup-confirm" />
            <Field label="Phone (optional)" v={form.phone} on={(v) => u('phone', v)} testid="signup-phone" />
            <Field label="Parent / guardian email (optional)" type="email" v={form.parent_email} on={(v) => u('parent_email', v)} testid="signup-parent-email" />
            <div className="sm:col-span-1">
              <Label>Program of interest</Label>
              <select value={form.program} onChange={(e) => u('program', e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="signup-program">
                {PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Field label="Subject / focus (optional)" v={form.subject_interest} on={(v) => u('subject_interest', v)} testid="signup-subject" />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200" data-testid="signup-error">
              <AlertCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-900">{error}</div>
            </div>
          )}

          <button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50" data-testid="signup-submit">
            {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Creating account…' : 'Create my account'}
          </button>
          <div className="text-[11px] text-gray-500 text-center">
            Already enrolled? <Link to="/student/login" className="underline text-gray-700">Sign in</Link>
          </div>
          <div className="text-[11px] text-gray-400 text-center">
            <Link to="/" className="hover:text-gray-700">← Back to Winhouse</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">{children}</label>;
const Field = ({ label, v, on, type = 'text', required, testid, hint }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={v} onChange={(e) => on(e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={testid} />
    {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default StudentSignupPage;
